import { CoreEnums } from '@/utils/domain/constants';

import { extractSqlNamedParameters } from './extractSqlNamedParameters';

const { ConnectionType } = CoreEnums;

export const isDatabaseConnectionType = (type) =>
  type === ConnectionType.Database || Number(type) === ConnectionType.Database;

export const getDatabaseConnectionQuery = (connection) => {
  const source = connection?.data || connection || {};
  const config = source.configurations || source.Configurations || {};
  return config.query || config.Query || source.query || '';
};

export const normalizeDatabaseParamName = (name) =>
  name == null ? '' : String(name).toLowerCase();

/**
 * Read a test value by first-seen casing or any case variant of the same name.
 *
 * @param {Record<string, unknown>} values
 * @param {string} name
 * @returns {unknown}
 */
export const getValidationParameterValue = (values, name) => {
  if (!values || name == null) {
    return undefined;
  }
  if (Object.prototype.hasOwnProperty.call(values, name)) {
    return values[name];
  }
  const normalized = normalizeDatabaseParamName(name);
  const match = Object.keys(values).find(
    (key) => normalizeDatabaseParamName(key) === normalized,
  );
  return match != null ? values[match] : undefined;
};

/**
 * Build the validate-only payload. Empty inputs are sent as SQL NULL.
 * Keys use first-seen casing (without `@`). Values are looked up
 * case-insensitively. Returns `undefined` when there are no params.
 *
 * @param {string[]} paramNames
 * @param {Record<string, unknown>} values
 * @returns {Record<string, string|null>|undefined}
 */
export const buildDatabaseValidationParameters = (paramNames, values = {}) => {
  if (!Array.isArray(paramNames) || paramNames.length === 0) {
    return undefined;
  }

  return paramNames.reduce((acc, name) => {
    const raw = getValidationParameterValue(values, name);
    if (raw == null || raw === '') {
      acc[name] = null;
    } else {
      acc[name] = raw;
    }
    return acc;
  }, {});
};

export const getDatabaseQueryParameterNames = (query) => extractSqlNamedParameters(query);

/**
 * Prompt only when the caller has not already collected test values.
 * An empty object still counts as "already collected" (query has no params).
 */
export const shouldPromptDatabaseValidationParameters = ({
  connectionType,
  validationParameters,
} = {}) => {
  if (!isDatabaseConnectionType(connectionType)) {
    return false;
  }
  return validationParameters === undefined;
};

export const toValidateConnectionBody = (validationParameters) => {
  if (validationParameters && Object.keys(validationParameters).length > 0) {
    return { validationParameters };
  }
  return {};
};
